import React, { useEffect, useRef, useState } from 'react';

/**
 * Square avatar cropper. The whole photo is always shown (contain-scaled to
 * fit the stage); the user drags a square selection box over it and resizes
 * it with the slider. Outside the box is dimmed (a huge box-shadow on the box
 * itself), inside is the full-opacity photo, so it's obvious what will be
 * kept. onSave reports { file, crop } where `crop` is the selection in
 * source-image pixels — always inside the image bounds, since the box can't
 * leave the stage. The full image is uploaded and cropped server-side / at
 * display time, so re-cropping an existing avatar (file === null) needs no
 * re-upload.
 *
 * No cropping dependency — plain pointer events + a little math.
 */

const MIN_FRAC = 0.15; // smallest selection, as a fraction of the largest possible square
const MAX_STAGE_W = 720;
const MAX_STAGE_H = 640;

// Leaves room for the modal's title/description/slider/buttons around the
// stage, so the whole card — sized to the image — still fits the screen. The
// width budget must stay inside the card's own `max-width: 95vw` (minus its
// horizontal padding), otherwise the stage ends up wider than the card and
// spills out of it.
const stageSize = () => ({
  w: typeof window !== 'undefined' ? Math.min(MAX_STAGE_W, window.innerWidth * 0.95 - 48) : MAX_STAGE_W,
  h: typeof window !== 'undefined' ? Math.min(MAX_STAGE_H, window.innerHeight * 0.9 - 260) : MAX_STAGE_H,
});

export default function AvatarCropModal({ file, src, onCancel, onSave, saving }) {
  const [img, setImg] = useState(null); // HTMLImageElement
  const [error, setError] = useState('');
  const [frac, setFrac] = useState(1); // selection size, 0..1 of the max square
  const [box, setBox] = useState({ x: 0, y: 0 }); // selection top-left, display px
  const drag = useRef(null); // { startX, startY, ox, oy } while dragging the box

  const stage = stageSize();
  const dispScale = img ? Math.min(stage.w / img.naturalWidth, stage.h / img.naturalHeight) : 1;
  const dispW = img ? img.naturalWidth * dispScale : stage.w;
  const dispH = img ? img.naturalHeight * dispScale : stage.h;
  const maxBox = Math.min(dispW, dispH); // largest square that fits the shown photo
  const boxSize = Math.max(24, maxBox * frac);

  const clampBox = (pos, size) => ({
    x: Math.min(Math.max(0, pos.x), Math.max(0, dispW - size)),
    y: Math.min(Math.max(0, pos.y), Math.max(0, dispH - size)),
  });

  // Decode the source (a picked File, or an existing avatar URL for re-crop)
  // into an <img>. A picked file is read as a data URL (no object-URL revoke
  // lifecycle, so it can't 404 mid-render); an existing avatar loads straight
  // from its URL. Nothing is drawn to a canvas — we only read the natural
  // size and report crop coordinates — so no CORS needed.
  useEffect(() => {
    let alive = true;
    const fail = () =>
      alive && setError('No hemos podido cargar la imagen para recortarla.');

    const load = (url) => {
      const image = new Image();
      image.onload = () => {
        if (!alive) return;
        setImg(image);
        setFrac(1);
      };
      image.onerror = fail;
      image.src = url;
    };

    if (file) {
      const reader = new FileReader();
      reader.onload = () => alive && load(reader.result);
      reader.onerror = fail;
      reader.readAsDataURL(file);
    } else if (src) {
      load(src);
    }
    return () => {
      alive = false;
    };
  }, [file, src]);

  // Center the selection whenever the image (or its displayed size) changes.
  useEffect(() => {
    if (!img) return;
    setBox({ x: (dispW - boxSize) / 2, y: (dispH - boxSize) / 2 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, dispW, dispH]);

  const onFrac = (e) => {
    const f = Number(e.target.value);
    const newSize = Math.max(24, maxBox * f);
    setFrac(f);
    setBox((prev) =>
      clampBox({ x: prev.x - (newSize - boxSize) / 2, y: prev.y - (newSize - boxSize) / 2 }, newSize)
    );
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, ox: box.x, oy: box.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setBox(
      clampBox(
        {
          x: drag.current.ox + (e.clientX - drag.current.startX),
          y: drag.current.oy + (e.clientY - drag.current.startY),
        },
        boxSize
      )
    );
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const handleSave = () => {
    if (!img) return;
    const crop = {
      x: Math.round(box.x / dispScale),
      y: Math.round(box.y / dispScale),
      width: Math.round(boxSize / dispScale),
      height: Math.round(boxSize / dispScale),
    };
    onSave({ file, crop });
  };

  return (
    <div className="Modal" role="presentation" onMouseDown={saving ? undefined : onCancel}>
      <div
        className="Modal__card AvatarCrop"
        role="dialog"
        aria-modal="true"
        aria-label="Recortar foto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="Modal__title">Ajusta tu foto</h2>
        <p className="Modal__desc">Mueve el recuadro y usa el control para elegir qué parte se verá.</p>
        {error && <p className="Modal__error">{error}</p>}

        <div className="AvatarCrop__stage" style={{ width: dispW, height: dispH }}>
          {img && (
            <>
              <img
                src={img.src}
                alt=""
                draggable={false}
                className="AvatarCrop__img"
                style={{ width: dispW, height: dispH }}
              />
              <div
                className="AvatarCrop__box"
                style={{ left: box.x, top: box.y, width: boxSize, height: boxSize }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
            </>
          )}
        </div>

        <input
          className="AvatarCrop__zoom"
          type="range"
          min={MIN_FRAC}
          max="1"
          step="0.01"
          value={frac}
          onChange={onFrac}
          aria-label="Tamaño de selección"
          disabled={!img}
        />

        <div className="Modal__actions">
          <button type="button" className="Btn Btn--soft" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="Btn Btn--primary" onClick={handleSave} disabled={!img || saving}>
            {saving ? 'Guardando…' : 'Guardar foto'}
          </button>
        </div>
      </div>
    </div>
  );
}
